import {MemberAddressListItem} from "@/models/member-address-list"
import {ColumnDef} from "@tanstack/react-table"
import {useEffect, useState} from "react"
import {useDebounce} from "@/hooks/use-debounce"
import {DataTable} from "@/components/data-table.tsx"

interface MemberAddressListDataTableProps {
    columns: ColumnDef<MemberAddressListItem, unknown>[]
    data: MemberAddressListItem[]
    searchTerm?: string
}

export function MemberAddressListDataTable({columns, data, searchTerm = ""}: MemberAddressListDataTableProps) {
    const [filteredData, setFilteredData] = useState<MemberAddressListItem[]>(data)

    const debouncedSearchTerm = useDebounce(searchTerm, 300)

    useEffect(() => {
        if (!debouncedSearchTerm.trim()) {
            setFilteredData(data)
            return
        }

        const filtered = data.filter(member => {
            const searchLower = debouncedSearchTerm.toLowerCase()

            const memberNoMatch = member.memberNo.toString().includes(debouncedSearchTerm)
            const nameMatch = `${member.firstName} ${member.lastName}`
                .toLowerCase()
                .includes(searchLower)
            const streetMatch = member.street?.toLowerCase().includes(searchLower)
            const cityMatch = member.city?.toLowerCase().includes(searchLower)
            const postcodeMatch = member.postcode?.includes(debouncedSearchTerm)
            const districtMatch = member.district?.toLowerCase().includes(searchLower)
            const salutationMatch = member.salutation?.toLowerCase().includes(searchLower)
            const titleMatch = member.title?.toLowerCase().includes(searchLower)

            return memberNoMatch || nameMatch || streetMatch || cityMatch
                || postcodeMatch || districtMatch || salutationMatch || titleMatch
        })

        setFilteredData(filtered)
    }, [debouncedSearchTerm, data])

    return (
        <div className="space-y-4">
            <DataTable
                columns={columns}
                data={filteredData}
                resetPageKey={debouncedSearchTerm}
                data-testid="address-list-table"
            />
        </div>
    )
}
